/* @flow */

import * as React from 'react';
import { get } from 'lodash/fp';
import { connect } from 'react-redux';
import { css } from 'glamor';
import styled from '@emotion/styled';
import { position } from 'polished';

import { userSignOut } from 'app/actions/userActions';
import MobileMenuList from 'app/components/menu/MobileMenuList';
import * as cssEase from 'app/constants/cssEase';

type Props = {
  avatar?: Object,
  hideLogin: boolean,
  isOpen?: boolean,
  isPxcEnabled?: boolean,
  isSignedIn: boolean,
  logoImage?: string,
  onClose: () => void,
  onSignOut: () => void,
  offsetTop?: number,
  onTapContentLink: string => void,
  userName?: string
};

type State = {
  isShowingAccountMenu: boolean,
  scrollDirection: 'down' | 'up'
};

const noScroll = css({
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0
}).toString();

const Menu = styled.div(({ isOpen, offsetTop, theme }) => ({
  backgroundColor: theme.color.basis2,
  overflowX: 'hidden',
  overflowY: 'scroll',
  ...position('fixed', offsetTop || '0', '15%', '0', '0'),
  transform: isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(-100%, 0, 0)',
  transition: 'transform 0.20s',
  transitionTimingFunction: cssEase.Circ.easeInOut,
  WebkitOverflowScrolling: 'auto', // there is a mobile safari bug with 'touch' when scrolling a div
  zIndex: theme.zIndex.menu
}));

class MobileMenu extends React.Component<Props, State> {
  props: Props;

  state: State = {
    isShowingAccountMenu: false,
    scrollDirection: 'up'
  };

  static defaultProps = {
    avatar: undefined,
    logoImage: undefined,
    isOpen: false,
    isPxcEnabled: false,
    offsetTop: 0,
    userName: undefined
  };

  prevScrollTop = 0; // eslint-disable-line react/sort-comp

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isSignedIn, isOpen } = this.props;

    if ((!isOpen && nextProps.isOpen) || (isSignedIn && !nextProps.isSignedIn)) {
      this.setState({
        isShowingAccountMenu: false
      });
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    // $FlowIgnore
    const { isOpen } = this.props;
    return isOpen || nextProps.isOpen;
  }

  componentDidUpdate(prevProps: Props) {
    const { isOpen } = this.props;

    if (isOpen && !prevProps.isOpen) {
      // $FlowIgnore
      document.body.classList.add(noScroll);
    } else if (!isOpen && prevProps.isOpen) {
      // $FlowIgnore
      document.body.classList.remove(noScroll);
    }
  }

  componentWillUnmount() {
    const { isOpen, onClose } = this.props;

    if (isOpen) {
      onClose();
      // $FlowIgnore
      document.body.classList.remove(noScroll);
    }
  }

  handleTouchStart = (e: { changedTouches: Array<Object> }) => {
    this.prevScrollTop = e.changedTouches[0].clientY;
  };

  handleTouchMove = (e: { changedTouches: Array<Object> }) => {
    const scrollTop = e.changedTouches[0].clientY;
    const threshold = 20;
    const diff = this.prevScrollTop - scrollTop;

    if (Math.abs(diff) > threshold) {
      this.prevScrollTop = scrollTop;
      this.setState({
        isShowingAccountMenu: false,
        scrollDirection: diff < 0 ? 'up' : 'down'
      });
    }
  };

  handleAccountClicked = () => {
    this.setState((state: State) => ({
      isShowingAccountMenu: !state.isShowingAccountMenu
    }));
  };

  render() {
    const { isOpen, isSignedIn, offsetTop, onClose, onTapContentLink } = this.props;

    return (
      <Menu
        isOpen={isOpen}
        offsetTop={offsetTop}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
      >
        <MobileMenuList
          isSignedIn={isSignedIn}
          onClose={onClose}
          onTapContentLink={onTapContentLink}
        />
      </Menu>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    avatar: get('avatar', state.user.currentUser),
    hideLogin: state.settings.hideLogin,
    isOpen: state.ui.isMobileMenuOpen,
    isSignedIn: state.user.isSignedIn,
    isPxcEnabled: !!state.settings.enablePxc,
    logoImage: state.settings.logoImage,
    userName: get('name', state.user.currentUser)
  };
}

const actions = {
  onSignOut: userSignOut
};

export default connect(mapStateToProps, actions)(MobileMenu);
